import { MOBILE_BRIDGE_QUERY_PARAM_NAME } from 'src/constants/common';
import { dataDog } from 'src/utils/datadog/data-dog';

const MOBILE_BRIDGE_ACTIONS = {
  INITIALIZATION_DATA: 'initializationData',
  ON_CHANGE_DIMENSIONS: 'onChangeDimensions',
  ON_LEARN_MORE_CLICKED: 'onLearnMoreClicked',
} as const;

interface Dimensions {
  width: number;
  height: number;
}

const AWAITING_TIMEOUT = 500;

const iosMessageHandlers = window?.webkit?.messageHandlers;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export class MobileBridge {
  private readonly mobileBridgeBus =
    iosMessageHandlers?.tabbyMobileSDK ||
    window?.tabbyMobileSDK ||
    iosMessageHandlers?.ReactNativeWebView ||
    window?.ReactNativeWebView;

  public sendDimensions(dimensions: Dimensions) {
    // this.mobileBridgeBus?.postMessage(JSON.stringify(dimensions), '*');
    this.sendDimensionsToAndroid(dimensions);
    this.sendDimensionsToIOS(dimensions);
  }

  private sendDimensionsToAndroid(dimensions: Dimensions) {
    this.mobileBridgeBus?.onChangeDimensions?.(JSON.stringify(dimensions));
  }

  private sendDimensionsToIOS(dimensions: Dimensions) {
    // iOS and flutter have same contract
    this.mobileBridgeBus?.postMessage?.(
      JSON.stringify({
        type: MOBILE_BRIDGE_ACTIONS.ON_CHANGE_DIMENSIONS,
        data: dimensions,
      })
    );
  }

  public openPage(url: string, data: Record<string, unknown> = {}) {
    const markedUrl = this.markUrl(url);

    try {
      const serializedData = JSON.stringify(data);

      this.openPageAndroid(serializedData, markedUrl);
      this.openPageIOS(serializedData, markedUrl);

      // this.mobileBridgeBus?.postMessage(serializedPayload, '*');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('MobileBridge serialize error', error);

      dataDog.error('MobileBridge serialize error', error);
    }
  }

  private openPageAndroid(data: string, url: string) {
    this.mobileBridgeBus?.onLearnMoreClicked?.(
      JSON.stringify({
        url,
        data,
      })
    );
  }

  private openPageIOS(data: string, url: string) {
    // iOS and flutter have same contract
    this.mobileBridgeBus?.postMessage?.(
      JSON.stringify({
        type: MOBILE_BRIDGE_ACTIONS.ON_LEARN_MORE_CLICKED,
        url,
        data,
      })
    );
  }

  public awaitData(): Promise<Record<string, unknown>> {
    return new Promise((resolve, reject) => {
      let timeoutTimerID: ReturnType<typeof setTimeout> | undefined;
      let handleLoad: () => void;
      let handleMessage: (event: MessageEvent) => void;

      const cleanup = () => {
        window.removeEventListener('load', handleLoad);
        window.removeEventListener('message', handleMessage);

        if (timeoutTimerID !== undefined) {
          clearTimeout(timeoutTimerID);
        }
      };

      const resolveData = (data: Record<string, unknown>) => {
        cleanup();
        resolve(data);
      };

      const rejectWith = (error: Error) => {
        cleanup();
        reject(error);
      };

      const startTimeout = () => {
        if (timeoutTimerID !== undefined) return;

        timeoutTimerID = setTimeout(() => {
          rejectWith(new Error('timeout'));
        }, AWAITING_TIMEOUT);
      };

      handleLoad = startTimeout;

      handleMessage = (event: MessageEvent) => {
        const envelope = this.parseData(event.data);

        if (
          !isRecord(envelope) ||
          envelope.type !== MOBILE_BRIDGE_ACTIONS.INITIALIZATION_DATA
        ) {
          return;
        }

        const initializationData = this.parseData(envelope.data);

        if (isRecord(initializationData)) {
          resolveData(initializationData);
        }
      };

      const url = new URL(window.location.href);
      const isURLContainsInitParams = url.searchParams.has(
        MOBILE_BRIDGE_ACTIONS.INITIALIZATION_DATA
      );

      if (isURLContainsInitParams) {
        const initializationData = this.parseData(
          url.searchParams.get(MOBILE_BRIDGE_ACTIONS.INITIALIZATION_DATA)
        );

        if (isRecord(initializationData)) {
          resolveData(initializationData);
        } else {
          rejectWith(new Error('invalid initialization data'));
        }

        return;
      }

      window.addEventListener('message', handleMessage);

      if (document.readyState === 'complete') {
        startTimeout();
      } else {
        window.addEventListener('load', handleLoad, { once: true });
      }
    });
  }

  private markUrl(url: string) {
    const newUrl = new URL(url);

    newUrl.searchParams.append(MOBILE_BRIDGE_QUERY_PARAM_NAME, '1');

    return newUrl.toString();
  }

  private parseData(data: unknown): unknown {
    if (!data || typeof data !== 'string') {
      return undefined;
    }

    try {
      return JSON.parse(data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('MobileBridge parse data error', error);

      dataDog.error('MobileBridge parse data error', error);

      return undefined;
    }
  }
}

export const mobileBridge = new MobileBridge();
